/**
 * useCmsLectureTopics — the 「可預約主題」 list, fresher than the bundle.
 *
 * Mirrors `useCmsLectures` exactly: the build-time snapshot
 * (`lecture-topics.generated.ts`) seeds the state so the prerendered HTML and
 * the no-JS case are complete, and a runtime read of
 * `/api/cms/public/lecture-topics` replaces it on mount so a topic published
 * in the CMS appears without a redeploy.
 *
 * The one difference from the lectures hook: an EMPTY live list is a
 * legitimate operator state here (the operator may have unpublished every
 * topic while the section is being reworked), so — unlike lectures, where an
 * empty list is treated as "CMS not migrated yet" — we do not silently keep
 * the stale snapshot. We keep it only when the request itself fails.
 *
 * PER-LOCALE PICKING
 * ------------------
 * The payload carries both the locale-collapsed fields and the per-locale
 * ones. This hook resolves the locale itself so the caller can use plain
 * `topic.title` regardless of which locale is active — and so the runtime
 * response and the prerendered snapshot produce identical text.
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  cmsLectureTopics,
  type CmsTopicIndexEntry,
} from '@/content/cms/lecture-topics.generated'
import { previewTokenParam } from '@/utils/previewToken'
import { DEFAULT_LOCALE, type Locale } from '@/utils/localePath'

interface TopicsResp {
  topics?: CmsTopicIndexEntry[]
}

/**
 * Resolve a topic's text for a locale.
 *
 * Chain: requested locale → zh-Hant → en. The server has already applied the
 * same chain plus OpenCC when it produced the collapsed `title`, so a
 * Simplified reader that hits the empty `_zhHans` column gets the converted
 * `title` rather than the raw Traditional `title_zhHant`.
 */
function pick(
  locale: Locale,
  item: CmsTopicIndexEntry,
  field: 'title' | 'description' | 'audience',
): string {
  const suffix = locale === 'zh-Hant' ? 'zhHant' : locale === 'zh-Hans' ? 'zhHans' : 'en'
  const direct = item[`${field}_${suffix}` as keyof CmsTopicIndexEntry]
  if (typeof direct === 'string' && direct.length > 0) return direct
  // Fall back to the server-resolved collapsed field, which already carries
  // the OpenCC conversion for the requested locale.
  const collapsed = item[field]
  return typeof collapsed === 'string' ? collapsed : ''
}

/** A topic with its locale already resolved — the shape the page renders. */
export interface ResolvedTopic {
  id: number
  number: string
  audience: string
  title: string
  description: string
  /** uuid of the feature image in cms_media, if the topic has one. */
  imageMediaUuid?: string
  imageAlt?: string
}

export function useCmsLectureTopics(): ResolvedTopic[] {
  const { i18n } = useTranslation()
  const locale = (i18n.language || DEFAULT_LOCALE) as Locale
  const [items, setItems] = useState<CmsTopicIndexEntry[]>(cmsLectureTopics)

  useEffect(() => {
    const controller = new AbortController()
    let cancelled = false

    void fetch(
      `/api/cms/public/lecture-topics?locale=${encodeURIComponent(locale)}${previewTokenParam()}`,
      { signal: controller.signal, credentials: 'same-origin' },
    )
      .then((r) => (r.ok ? (r.json() as Promise<TopicsResp>) : null))
      .then((j) => {
        if (cancelled) return
        if (!j?.topics) return
        setItems(j.topics)
      })
      .catch(() => {
        // Network error, abort, or non-JSON response. The snapshot stays.
      })

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [locale])

  return items.map((item) => ({
    id: item.id,
    number: item.number,
    audience: pick(locale, item, 'audience'),
    title: pick(locale, item, 'title'),
    description: pick(locale, item, 'description'),
    imageMediaUuid: item.image_media_uuid ?? undefined,
    imageAlt: item.image_alt || pick(locale, item, 'title') || undefined,
  }))
}
